import React, { useEffect, useState } from "react";
import { Card, CardContent, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useDataProvider } from "react-admin";
import { useTheme } from "@mui/material/styles";

interface SloGaugeChartProps {
  releaseName: string;
  releaseVersion: string;
  sloField: "adjudicationSloDate" | "fixSloDate";
}

const SloGaugeChart: React.FC<SloGaugeChartProps> = ({
  releaseName,
  releaseVersion,
  sloField,
}) => {
  const theme = useTheme();
  const [data, setData] = useState<{ name: string; value: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const dataProvider = useDataProvider();

  useEffect(() => {
    const defaultData = [
      {
        name: "7+ days",
        value: 0,
        itemStyle: { color: "rgb(100, 59, 177)" },
        detail: {
          width: "75em",
          offsetCenter: ["0%", "-30%"],
        },
      },
      {
        name: "< 7 days",
        value: 0,
        itemStyle: { color: "#F57C00" },
        detail: {
          width: "75em",
          offsetCenter: ["0%", "0%"],
        },
      },
      {
        name: "Breached",
        value: 0,
        itemStyle: { color: "#D32F2F" },
        detail: {
          width: "75em",
          offsetCenter: ["0%", "30%"],
        },
      },
    ];
    let isMounted = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        // Use a custom method 'getSloGauge' on the dataProvider, or fallback to getOne with custom resource
        const response = await dataProvider.getChart(
          "cve/analytics/slo-chart",
          {
            releaseName,
            releaseVersion,
            sloField,
          }
        );
        if (isMounted) {
          setData(
            defaultData.map((d) => ({
              ...d,
              value:
                response.find(
                  (r: { name: string; value: number }) => r.name === d.name
                )?.value || 0,
            }))
          );
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError("Failed to fetch SLO data");
          setLoading(false);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [dataProvider, releaseName, releaseVersion, sloField]);

  const option = {
    title: {
      text: sloField === "adjudicationSloDate" ? "Adjudication SLO" : "Fix SLO",
      left: "center",
      textStyle: {
        color: theme.palette.mode === "dark" ? "#fff" : "#222",
        fontWeight: "bold",
        fontSize: "1.5em",
      },
    },
    series: [
      {
        type: "gauge",
        center: ["50%", "55%"],
        startAngle: 90,
        endAngle: -270,
        min: 0,
        max: data.reduce((sum, d) => sum + d.value, 0),
        pointer: {
          show: false,
        },
        progress: {
          show: true,
          overlap: false,
          roundCap: true,
          clip: false,
        },
        axisLine: {
          lineStyle: {
            width: 25,
            opacity: 0.75,
          },
        },
        splitLine: {
          show: false,
          distance: 0,
          length: 10,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
          distance: 50,
        },
        data: data,
        title: {
          fontSize: "1.2em",
          color: theme.palette.mode === "dark" ? "#fff" : "#222",
          show: false,
        },
        detail: {
          width: "40%",
          height: "15%",
          fontSize: "1em",
          color: "#fff",
          backgroundColor: "inherit",
          borderRadius: 10,
          textBorderColor: "#544f5a",
          textBorderWidth: 4,
        },
      },
    ],
    tooltip: {
      formatter: () =>
        data.map((d) => `${d.name}: ${d.value}`).join("<br>") || "No data",
    },
  };

  return (
    <Card>
      <CardContent>
        {loading ? (
          <Typography>Loading...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <ReactECharts
            option={option}
            style={{ height: "250px", width: "100%" }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default SloGaugeChart;
